import { Grid } from 'antd';

/**
 * Custom hook for responsive design
 * Returns breakpoint information and device type detection
 *
 * Usage:
 * const { isMobile, isTablet, isDesktop, screens } = useResponsive();
 */
export const useResponsive = () => {
  const screens = Grid.useBreakpoint();

  return {
    // Device type detection
    isMobile: !screens.md,                    // < 768px
    isTablet: screens.md && !screens.lg,      // 768px - 991px
    isDesktop: screens.lg,                    // >= 992px
    isLargeDesktop: screens.xl,               // >= 1200px
    isExtraLargeDesktop: screens.xxl,         // >= 1600px

    // Specific breakpoints
    isXs: screens.xs,           // >= 0px
    isSm: screens.sm,           // >= 576px
    isMd: screens.md,           // >= 768px
    isLg: screens.lg,           // >= 992px
    isXl: screens.xl,           // >= 1200px
    isXxl: screens.xxl,         // >= 1600px

    // Full screens object
    screens,
  };
};

/**
 * Get responsive size value based on current screen
 *
 * Usage:
 * const size = getResponsiveSize({ mobile: 'small', tablet: 'middle', desktop: 'large' });
 */
export const getResponsiveSize = ({ mobile, tablet, desktop }) => {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
};

/**
 * Get responsive column span for Ant Design Grid
 *
 * Usage:
 * const span = getResponsiveSpan({ xs: 24, sm: 12, md: 8, lg: 6 });
 */
export const getResponsiveSpan = (spans) => spans;

/**
 * Get responsive gutter (spacing) for Grid
 *
 * Usage:
 * const gutter = getResponsiveGutter();
 */
export const getResponsiveGutter = () => {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile) return [8, 8];      // 8px horizontal, 8px vertical
  if (isTablet) return [16, 16];    // 16px
  return [24, 24];                   // 24px
};

/**
 * Get responsive padding
 */
export const getResponsivePadding = () => {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile) return '12px';
  if (isTablet) return '16px';
  return '24px';
};

/**
 * Get responsive table page size
 */
export const getResponsiveTablePageSize = () => {
  const { isMobile, isTablet } = useResponsive();

  if (isMobile) return 5;
  if (isTablet) return 10;
  return 20;
};

/**
 * Check if touch device
 */
export const isTouchDevice = () => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Get responsive font size class
 */
export const getResponsiveFontClass = (type = 'body') => {
  const classes = {
    h1: 'text-2xl md:text-3xl lg:text-4xl',
    h2: 'text-xl md:text-2xl lg:text-3xl',
    h3: 'text-lg md:text-xl lg:text-2xl',
    h4: 'text-base md:text-lg lg:text-xl',
    h5: 'text-sm md:text-base lg:text-lg',
    body: 'text-sm md:text-base',
    small: 'text-xs md:text-sm',
  };

  return classes[type] || classes.body;
};

/**
 * Get responsive spacing class
 */
export const getResponsiveSpacingClass = (size = 'md') => {
  const classes = {
    xs: 'p-1 md:p-2 lg:p-2',
    sm: 'p-2 md:p-3 lg:p-4',
    md: 'p-4 md:p-5 lg:p-6',
    lg: 'p-6 md:p-8 lg:p-10',
    xl: 'p-8 md:p-10 lg:p-12',
  };

  return classes[size] || classes.md;
};

export default useResponsive;
